import type { PipeTransform } from '@angular/core';
import { Pipe, inject } from '@angular/core';
import type { Locale } from './translation.service';
import { TranslationService } from './translation.service';

/**
 * Memoized translate pipe.
 *
 * Kept as `pure: false` so Angular re-evaluates it on every CD cycle (required
 * because locale changes via Signal, not via pipe arguments). The memoization
 * cache ensures the actual translation lookup only runs when the key, params,
 * or locale have changed -- eliminating the performance cost of impure pipes.
 */
@Pipe({
  name: 'translate',
  standalone: true,
  pure: false,
})
export class TranslatePipe implements PipeTransform {
  private readonly translationService = inject(TranslationService);

  private cachedKey = '';
  private cachedParams: Record<string, string | number> | undefined;
  private cachedLocale: Locale | null = null;
  private cachedResult = '';

  transform(key: string, params?: Record<string, string | number>): string {
    const locale = this.translationService.locale();

    if (key === this.cachedKey && params === this.cachedParams && locale === this.cachedLocale) {
      return this.cachedResult;
    }

    this.cachedKey = key;
    this.cachedParams = params;
    this.cachedLocale = locale;
    this.cachedResult = this.translationService.t(key, params);
    return this.cachedResult;
  }
}
